/**
 * Welcome - Modern Dashboard Page (Light Theme)
 * 
 * Redesigned to show:
 * - Total Points (Geofences)
 * - Total Salesmen (Drivers)
 * - Active Units
 * - Salesmen Performance Table
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import {
    selectWialonSession,
} from '../../reduxStore/WialonSessionReducer';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import { getAccountStats } from '../../Network/Wialon/WialonAPI';
import WialonService from '../../Network/Wialon/WialonService';
import { processZones } from '../GeoExtractor/geoExtractorUtils';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import '../../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
`;

// Floating Shape Component
const FloatingShape = ({ size, top, left, right, delay, opacity = 0.1 }) => (
    <Box
        sx={{
            position: 'absolute',
            top,
            left,
            right,
            width: size,
            height: size,
            borderRadius: '50%',
            border: '2px solid #4ECDC4',
            opacity,
            animation: `${float} ${6 + delay}s ease-in-out infinite`,
            animationDelay: `${delay}s`,
            pointerEvents: 'none',
        }}
    />
);

// Stats Card Component
const StatsCard = ({ icon, title, value, subtitle, delay, gradient, onClick, loading }) => (
    <Box
        onClick={onClick}
        sx={{
            background: '#FFFFFF',
            borderRadius: 3,
            padding: 3,
            border: '1px solid #E2E8F0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.3s ease',
            animation: `${fadeInUp} 0.6s ease-out forwards`,
            animationDelay: `${delay}s`,
            opacity: 0,
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick ? {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                borderColor: '#4ECDC4',
            } : {},
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: 13,
                        color: '#64748B',
                        fontFamily: 'Cairo, sans-serif',
                        fontWeight: 600,
                        marginBottom: 1,
                    }}
                >
                    {title}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" width="60%" height={40} />
                ) : (
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 800,
                            background: gradient || 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontFamily: 'Cairo, sans-serif',
                            lineHeight: 1.2,
                        }}
                    >
                        {value}
                    </Typography>
                )}
                <Typography
                    sx={{
                        fontSize: 12,
                        color: '#94A3B8',
                        fontFamily: 'Cairo, sans-serif',
                        marginTop: 0.5,
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: gradient || 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                }}
            >
                {icon}
            </Box>
        </Box>
    </Box>
);

export default function Welcome() {
    const { t, i18n } = useTranslation('common');
    const isRTL = i18n.dir() === 'rtl';
    const navigate = useNavigate();
    const session = useSelector(selectWialonSession);
    const gridtheme = useSelector(state => state.themeData.gridtheme);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalPoints: 0,
        totalSalesmen: 0,
        activeUnits: 0,
        daysLeft: 0
    });
    const [salesmenStats, setSalesmenStats] = useState([]);

    const hasFetched = useRef(false);

    // Update time every minute
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Account Stats (Basic info)
                const accountRes = await getAccountStats();
                let basicStats = { activeUnits: 0, daysLeft: 0 };

                if (accountRes.status && accountRes.data && accountRes.data.data) {
                    const data = accountRes.data.data;
                    basicStats = {
                        activeUnits: data.active_units ?? 0,
                        daysLeft: data.account_status?.days_left ?? 0
                    };
                }

                // 2. Fetch All Geofences to calculate Points and Salesmen
                const sid = session?.sid || cookies.get('sid');
                const baseUrl = session?.baseUrl || cookies.get('baseUrl');

                if (sid && baseUrl) {
                    const resourcesRes = await WialonService.loadResources(baseUrl, sid);
                    if (resourcesRes && resourcesRes.items) {
                        let allEntries = [];
                        resourcesRes.items.forEach(res => {
                            if (res.zl) {
                                const { rows } = processZones(Object.values(res.zl), baseUrl, sid);
                                allEntries = [...allEntries, ...rows];
                            }
                        });

                        // Aggregate Salesmen Data
                        const salesmenMap = {};
                        allEntries.forEach(entry => {
                            const salesman = entry.mandoob || entry.driver || '----';
                            if (salesman === '----') return;

                            if (!salesmenMap[salesman]) {
                                salesmenMap[salesman] = {
                                    name: salesman,
                                    count: 0,
                                    lastPoint: entry.captureTime
                                };
                            }
                            salesmenMap[salesman].count++;

                            // Simple string comparison for "latest" if we trust the format
                            if (entry.captureTime > salesmenMap[salesman].lastPoint) {
                                salesmenMap[salesman].lastPoint = entry.captureTime;
                            }
                        });

                        const salesmenList = Object.values(salesmenMap).sort((a, b) => b.count - a.count);

                        setStats({
                            ...basicStats,
                            totalPoints: allEntries.length,
                            totalSalesmen: salesmenList.length
                        });
                        setSalesmenStats(salesmenList);
                    }
                } else {
                    setStats({ ...basicStats, totalPoints: 0, totalSalesmen: 0 });
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [session]);

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return t('WELCOME.goodMorning') || 'Good Morning';
        if (hour < 18) return t('WELCOME.goodAfternoon') || 'Good Afternoon';
        return t('WELCOME.goodEvening') || 'Good Evening';
    };

    const userName = session?.userName || cookies.get('user_name') || 'User';
    const accountName = session?.accountName || cookies.get('account_name') || '';

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 120px)',
                background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)',
                borderRadius: 3,
                padding: { xs: 2, md: 4 },
                position: 'relative',
                overflow: 'hidden',
                // direction: isRTL ? 'rtl' : 'ltr',
            }}
        >
            <FloatingShape size={80} top="5%" left="5%" delay={0} opacity={0.08} />
            <FloatingShape size={50} top="20%" right="10%" delay={1} opacity={0.06} />

            {/* Header */}
            <Box sx={{ mb: 4, animation: `${fadeInUp} 0.6s ease-out forwards` }}>
                <Typography sx={{ fontSize: 13, color: '#64748B', fontFamily: 'Cairo, sans-serif', mb: 0.5 }}>
                    {currentTime.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                </Typography>
                <Typography sx={{
                    fontSize: { xs: 24, md: 32 },
                    fontWeight: 800,
                    color: '#1E293B',
                    fontFamily: 'Cairo, sans-serif'
                }}>
                    {getGreeting()}, <span style={{ color: '#4ECDC4' }}>{userName}</span>
                </Typography>
                {accountName && (
                    <Typography sx={{ fontSize: 15, color: '#64748B', fontFamily: 'Cairo, sans-serif', mt: 0.5 }}>
                        {accountName}
                    </Typography>
                )}
            </Box>

            {/* Main Stats */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 3,
                mb: 6,
                zIndex: 1,
                position: 'relative'
            }}>
                <StatsCard
                    icon={<PlaceIcon sx={{ color: '#fff', fontSize: 24 }} />}
                    title={isRTL ? 'إجمالي النقاط' : 'Total Points'}
                    value={stats.totalPoints}
                    subtitle={isRTL ? 'النقاط المستخرجة من الخريطة' : 'Points extracted from map'}
                    delay={0.1}
                    loading={loading}
                    onClick={() => navigate('/system/geo-extractor')}
                    gradient="linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)"
                />
                <StatsCard
                    icon={<PeopleIcon sx={{ color: '#fff', fontSize: 24 }} />}
                    title={isRTL ? 'إجمالي المندوبين' : 'Total Salesmen'}
                    value={stats.totalSalesmen}
                    subtitle={isRTL ? 'المندوبين النشطين في الميدان' : 'Active salesmen in field'}
                    delay={0.2}
                    loading={loading}
                    gradient="linear-gradient(135deg, #6C9BCF 0%, #667EEA 100%)"
                />
                <StatsCard
                    icon={<TrendingUpIcon sx={{ color: '#fff', fontSize: 24 }} />}
                    title={isRTL ? 'الوحدات النشطة' : 'Active Units'}
                    value={stats.activeUnits}
                    subtitle={isRTL ? 'عدد السيارات المتصلة حالياً' : 'Currently active units'}
                    delay={0.3}
                    loading={loading}
                    gradient="linear-gradient(135deg, #FF6B6B 0%, #EE5253 100%)"
                />
            </Box>

            {/* Salesmen Performance Section */}
            <Box sx={{ animation: `${fadeInUp} 0.6s ease-out forwards`, animationDelay: '0.4s', opacity: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1E293B', fontFamily: 'Cairo, sans-serif' }}>
                        {isRTL ? 'أداء المندوبين' : 'Salesmen Performance'}
                    </Typography>
                    <Chip
                        label={loading ? '...' : `${salesmenStats.length} ${isRTL ? 'مندوب' : 'Salesmen'}`}
                        size="small"
                        sx={{ background: 'rgba(78, 205, 196, 0.1)', color: '#4ECDC4', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}
                    />
                </Box>

                <Paper sx={{
                    borderRadius: 3,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden'
                }}>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderLeft: 'none !important', borderRight: 'none !important' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#FFFFFF', fontFamily: 'Cairo, sans-serif', fontSize: 13, background: gridtheme?.primaryColor || '#707070' }}>
                                        {isRTL ? 'المندوب' : 'Salesman'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#FFFFFF', fontFamily: 'Cairo, sans-serif', fontSize: 13, background: gridtheme?.primaryColor || '#707070' }}>
                                        {isRTL ? 'عدد النقاط' : 'Total Points'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#FFFFFF', fontFamily: 'Cairo, sans-serif', fontSize: 13, background: gridtheme?.primaryColor || '#707070' }}>
                                        {isRTL ? 'آخر تحديث' : 'Last Update'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell ><Skeleton variant="text" width="60%" /></TableCell>
                                            <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                                            <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : salesmenStats.length > 0 ? (
                                    salesmenStats.map((salesman) => (
                                        <TableRow key={salesman.name} sx={{ '&:hover': { background: '#F8FAFC' } }}>
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{
                                                        width: 32, height: 32,
                                                        fontSize: 14,
                                                        bgcolor: '#4ECDC4',
                                                        fontFamily: 'Cairo, sans-serif'
                                                    }}>
                                                        {salesman.name.charAt(0)}
                                                    </Avatar>
                                                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontFamily: 'Cairo, sans-serif', fontSize: 14 }}>
                                                        {salesman.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography sx={{ fontWeight: 800, color: '#4ECDC4', fontFamily: 'Cairo, sans-serif' }}>
                                                    {salesman.count}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: '#94A3B8' }}>
                                                    <HistoryIcon sx={{ fontSize: 16 }} />
                                                    <Typography sx={{ fontSize: 12, fontWeight: 500, fontFamily: 'Cairo, sans-serif' }}>
                                                        {salesman.lastPoint}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: '#94A3B8', fontFamily: 'Cairo, sans-serif' }}>
                                            {isRTL ? 'لا توجد بيانات متاحة' : 'No data available'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Session Status Placeholder (Optional replacement) */}
            <Box
                sx={{
                    mt: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 14px',
                    background: 'rgba(16, 185, 129, 0.08)',
                    borderRadius: 2,
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    display: 'inline-flex'
                }}
            >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)' }} />
                <Typography sx={{ fontSize: 12, color: '#10B981', fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                    {isRTL ? 'الجلسة نشطة' : 'Session Active'}
                </Typography>
            </Box>
        </Box>
    );
}